import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { Button, Card, Col, Container, Form, Row } from '@themesberg/react-bootstrap';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Routes } from '../../routes';

const API_BASE = (process.env.REACT_APP_API_URL || 'https://api.flowfence.io').replace(/\/$/, '');

const STARTER_RULE = {
  name: 'Login attempts per email',
  eventType: 'auth.login_attempt',
  condition: {
    aggregate: 'COUNT',
    groupBy: 'subjects.email',
    window: 'PT1M',
    operator: '>=',
    threshold: 5,
  },
  decision: 'BLOCK',
};

const DECIDE_SAMPLE = {
  eventType: 'auth.login_attempt',
  entity: { type: 'user', id: 'user_789' },
  subjects: {
    userId: 'user_789',
    email: 'user@example.com',
    phone: '+15555550100',
    deviceId: 'dev_abc',
    ip: '1.2.3.4',
    sessionId: 'sess_001',
  },
  attributes: { country: 'US', channel: 'web', result: 'failed' },
  value: { amount: null, currency: null },
};

function CopyBlock({ label, value }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  return (
    <div className="mb-3">
      {label ? <Form.Label className="fw-bold">{label}</Form.Label> : null}
      <div className="position-relative">
        <pre
          className="bg-light border rounded p-3 small mb-2"
          style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
        >
          {value}
        </pre>
        <Button size="sm" variant="outline-primary" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </Button>
      </div>
    </div>
  );
}

function Step({ number, title, children }) {
  return (
    <Card border="light" className="shadow-sm mb-4">
      <Card.Body>
        <h5 className="mb-3">
          <span className="badge bg-primary me-2">{number}</span>
          {title}
        </h5>
        {children}
      </Card.Body>
    </Card>
  );
}

export default function Developers() {
  const { isAuthenticated, tenantId, clientSecret } = useAuth();
  const secretInCurl = clientSecret || 'YOUR_CLIENT_SECRET';
  const tid = tenantId || 'YOUR_TENANT_ID';

  const tokenCurl = `curl -sS -X POST '${API_BASE}/v1/auth/token' \\
  -H 'Content-Type: application/json' \\
  -d '{
    "tenantId": "${tid}",
    "clientSecret": "${secretInCurl}"
  }'`;

  const createRuleCurl = `curl -sS -X POST '${API_BASE}/v1/velocity/rules' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  -d '${JSON.stringify(STARTER_RULE, null, 2)}'`;

  const decideCurl = `curl -sS -X POST '${API_BASE}/v1/decide' \\
  -H 'Content-Type: application/json' \\
  -H 'Authorization: Bearer YOUR_ACCESS_TOKEN' \\
  -d '${JSON.stringify({ ...DECIDE_SAMPLE, tenantId: tid === 'YOUR_TENANT_ID' ? undefined : tid }, null, 2)}'`;

  return (
    <main>
      <Container className="py-5">
        <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
          <Link to={Routes.Welcome.path} className="d-flex align-items-center text-decoration-none">
            <FontAwesomeIcon icon={faShieldAlt} className="text-primary me-2" />
            <span className="fw-bold text-primary">Flowfence</span>
          </Link>
          <div className="d-flex align-items-center gap-3">
            {isAuthenticated ? (
              <Link to={Routes.Dashboard.path} className="text-gray small">
                Open console
              </Link>
            ) : (
              <>
                <Link to={Routes.Login.path} className="text-gray small">
                  Sign in
                </Link>
                <Link to={Routes.Register.path} className="text-gray small">
                  Start free
                </Link>
              </>
            )}
            <Link to={Routes.Welcome.path} className="text-gray small">
              <FontAwesomeIcon icon={faAngleLeft} className="me-1" />
              Back to homepage
            </Link>
          </div>
        </div>

        <Row className="justify-content-center mb-4">
          <Col xs={12} lg={10}>
            <h1 className="h3 mb-2">API quickstart</h1>
            <p className="text-gray mb-0">
              Go from credentials to a real <code>/v1/decide</code> call in four steps. No login
              required to read this page — register when you are ready to run the curls.
            </p>
          </Col>
        </Row>

        <Row className="justify-content-center">
          <Col xs={12} lg={10}>
            <div className="alert alert-info" role="alert">
              Access tokens expire in about <strong>15 minutes</strong>. There is no refresh endpoint —
              call <code>POST /v1/auth/token</code> again with your client secret.
            </div>

            <div className="mb-4">
              <a
                className="btn btn-sm btn-outline-secondary me-2 mb-2"
                href={`${API_BASE}/swagger-ui/index.html`}
                target="_blank"
                rel="noreferrer"
              >
                OpenAPI / Swagger UI
              </a>
              <a
                className="btn btn-sm btn-outline-secondary mb-2"
                href="/postman/flowfence.postman_collection.json"
                download
              >
                Postman collection
              </a>
              <p className="small text-muted mb-0">
                After importing, set collection variable <code>baseUrl</code> to{' '}
                <code>https://api.flowfence.io</code> or <code>http://localhost:8080</code>.
              </p>
            </div>

            <Step number="1" title="Credentials">
              <p className="text-gray">
                <Link to={Routes.Register.path}>Register</Link> a workspace to get a{' '}
                <code>tenantId</code> and one-time client secret. Sign in to pre-fill them here, or
                paste into the curls below. Rotate secrets later under Account (signed in).
              </p>
              <Form.Group className="mb-0">
                <Form.Label>Tenant ID {isAuthenticated ? '(from this browser)' : '(placeholder)'}</Form.Label>
                <Form.Control readOnly value={tid} />
              </Form.Group>
              {isAuthenticated && clientSecret ? (
                <p className="small text-muted mt-2 mb-0">
                  Client secret from this browser session is filled into the token curl below.
                </p>
              ) : (
                <p className="small text-muted mt-2 mb-0">
                  Replace <code>YOUR_TENANT_ID</code> / <code>YOUR_CLIENT_SECRET</code> after you
                  register.
                </p>
              )}
            </Step>

            <Step number="2" title="Get a JWT">
              <p className="text-gray">
                Exchange tenant credentials for an access token. Put the <code>accessToken</code>{' '}
                from the response into the next curls as <code>YOUR_ACCESS_TOKEN</code>.
              </p>
              <CopyBlock value={tokenCurl} />
            </Step>

            <Step number="3" title="Create a rule">
              <p className="text-gray">
                Without at least one qualifying velocity rule (or a blocklist hit),{' '}
                <code>/v1/decide</code> returns <strong>ALLOW</strong> with an empty{' '}
                <code>results</code> array — not a real rule test.
              </p>
              <p className="text-gray">
                {isAuthenticated ? (
                  <>
                    Use the <Link to={Routes.Rules.path}>Rules console</Link> or create this starter
                    rule via API.
                  </>
                ) : (
                  <>Create this starter rule via API after you have a JWT.</>
                )}{' '}
                It matches the decide sample in step 4 (<code>auth.login_attempt</code> +{' '}
                <code>subjects.email</code>).
              </p>
              <CopyBlock label="POST /v1/velocity/rules" value={createRuleCurl} />
            </Step>

            <Step number="4" title="Call decide">
              <p className="text-gray">
                Send an event. After the starter rule exists, repeated login attempts for the same
                email within one minute will move toward <strong>BLOCK</strong> (HTTP 429) once the
                threshold is hit.
                {isAuthenticated ? (
                  <>
                    {' '}
                    You can also try this in the <Link to={Routes.Decide.path}>Decision console</Link>.
                  </>
                ) : null}
              </p>
              <CopyBlock label="POST /v1/decide" value={decideCurl} />
            </Step>
          </Col>
        </Row>
      </Container>
    </main>
  );
}
